"use client";

import { useEffect, useState } from "react";

/**
 * Sticky scroll-spy table of contents. Highlights the section currently in
 * view via IntersectionObserver and smooth-scrolls on click. Render it inside
 * a `sticky` wrapper. `items` = [{ id, label }] matching section ids on the page.
 */
export default function TableOfContents({ items }) {
  const [active, setActive] = useState(items[0]?.id);

  useEffect(() => {
    const els = items
      .map((i) => document.getElementById(i.id))
      .filter(Boolean);
    if (!els.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-15% 0px -70% 0px", threshold: 0 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [items]);

  const onClick = (e, id) => {
    const el = document.getElementById(id);
    if (!el) return;
    e.preventDefault();
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    if (typeof history !== "undefined") history.replaceState(null, "", `#${id}`);
  };

  return (
    <nav aria-label="On this page" className="text-sm">
      <p className="mb-3 text-xs font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500">
        On this page
      </p>
      <ul className="space-y-1 border-l border-gray-200 dark:border-gray-700">
        {items.map((i) => (
          <li key={i.id}>
            <a
              href={`#${i.id}`}
              onClick={(e) => onClick(e, i.id)}
              className={`-ml-px block border-l-2 py-1.5 pl-4 leading-snug transition-colors ${
                active === i.id
                  ? "border-blue-600 font-semibold text-blue-700 dark:border-blue-400 dark:text-blue-300"
                  : "border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
              }`}
            >
              {i.label}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}
